import { Add, Remove, RemoveCircleOutline } from '@mui/icons-material';
import { Grid, Typography } from '@mui/material';
import classNames from 'classnames';
import React from 'react';
import { useDateFns, useLanguage } from '~/Hooks';
import { PopoverToModal } from '~/Components';
import { Clock } from '~/Components/Icons';
import { WeekDays } from '~/Models/DTO/Provider.model';
import { Button, IconButton, KeyboardTimePicker, Switch } from '~/UIKit';
import { OpeningHoursSectionStyles } from '../OpeningHoursSectionStyles';

interface OpeningHoursSectionDayModalProps {
  open: boolean;
  onClose: () => void;
  anchorEl?: null | Element;
  currentDay: string;
  onToggleDay: (day: string, timeIndex?: number) => void;
  displayWeekdays: WeekDays;
  errorWeekdays: any;
  onTimeChange: (day: string, timeRowIndex: number, type: 'from' | 'to', value: any) => void;
  onAddTime: (day: string) => void;
}

export const OpeningHoursSectionDayModal = ({
  open,
  onClose,
  anchorEl,
  currentDay,
  onToggleDay,
  displayWeekdays,
  errorWeekdays,
  onTimeChange,
  onAddTime,
}: OpeningHoursSectionDayModalProps) => {
  const { t } = useLanguage();
  const { classes } = OpeningHoursSectionStyles();
  const dataTable = displayWeekdays?.[currentDay];
  const { convertTimeStringToDate } = useDateFns();

  const handleAddTime = () => {
    onAddTime(currentDay);
  };

  return (
    <PopoverToModal
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      title={t(`app.weekdays.${currentDay}`)}
      onActionTaken={() => {}}
    >
      <>
        <Grid container>
          <Grid
            className={classNames(classes.dayRow, classes.hasBottomBorder)}
            alignItems="center"
            xs={6}
            container
          >
            <>
              <Grid className={classes.iconWrapper}>
                <Clock />
              </Grid>
              <span className={classes.itemRow}>{t('app.open')}</span>
            </>
          </Grid>
          <Grid
            container
            xs={6}
            justifyContent="flex-end"
            className={classNames(classes.row, classes.hasBottomBorder)}
          >
            <Switch checked={dataTable?.length > 0} onChange={() => onToggleDay(currentDay)} />
          </Grid>
        </Grid>
        {dataTable?.length > 0 &&
          dataTable.map((_dayRow, dayRowIndex) => (
            <Grid data-testid="opening-hours-table-row" container key={dayRowIndex}>
              <Grid
                className={classNames({
                  [classes.hasBottomBorder]: dayRowIndex === dataTable.length - 1,
                })}
                container
                xs={6}
                alignSelf="normal"
                alignItems="self-start"
              >
                <Grid
                  className={classNames(classes.dayRow)}
                  key={dayRowIndex}
                  alignItems="center"
                  container
                  xs={12}
                >
                  <Grid
                    className={classNames(classes.iconWrapper, {
                      [classes.hiddenLabel]: dayRowIndex > 0,
                    })}
                  >
                    <Clock />
                  </Grid>
                  <Grid
                    flex={1}
                    container
                    justifyContent="start"
                    alignItems="center"
                    className={classNames({
                      [classes.hasBottomBorder]: dayRowIndex + 1 < dataTable.length,
                    })}
                  >
                    <Typography>{`Slot ${dayRowIndex + 1}`}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                xs={6}
                container
                data-testid="opening-hours-section-time-block"
                className={classes.hasBottomBorder}
              >
                <Grid container xs={12}>
                  <Grid item xs={4}>
                    <KeyboardTimePicker
                      inputClasses={classes.timePicker}
                      inputFormat="HH:mm"
                      name={`fromName-${_dayRow}`}
                      error={
                        !errorWeekdays[currentDay]?.[dayRowIndex]?.isValid &&
                        errorWeekdays[currentDay]?.[dayRowIndex]?.type === 0
                      }
                      value={convertTimeStringToDate(_dayRow?.from?.toString()).toISOString() || ''}
                      onDateChange={(_, value) =>
                        onTimeChange(currentDay, dayRowIndex, 'from', value)
                      }
                    />
                  </Grid>
                  <Grid container xs={1} alignItems="center" justifyContent="center">
                    <Remove className={classes.separator} />
                  </Grid>
                  <Grid item xs={4}>
                    <KeyboardTimePicker
                      inputClasses={classes.timePicker}
                      inputFormat="HH:mm"
                      value={convertTimeStringToDate(_dayRow?.to?.toString()).toISOString() || ''}
                      onDateChange={(_, value) =>
                        onTimeChange(currentDay, dayRowIndex, 'to', value)
                      }
                    />
                  </Grid>
                  <Grid xs={3} alignItems="center" container justifyContent="center">
                    <IconButton
                      className={classes.deleteButton}
                      onClick={() => onToggleDay(currentDay, dayRowIndex)}
                      size="medium"
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ))}
        <Grid container>
          <Grid flex={1} container justifyContent="start" alignItems="center">
            {dataTable?.length > 0 && (
              <Button onClick={handleAddTime} variant="text" className={classes.addButtonText}>
                <Add />
                {t('form.field.location.addTime')}
              </Button>
            )}
          </Grid>
        </Grid>
      </>
    </PopoverToModal>
  );
};
